import type {
  BatchTransferResearch,
  BatchImportCatalogWarning,
  CardRecord,
  ChecklistIssueType,
} from '@tcvs/shared';

export function getImportedCardResearch(card: CardRecord | null | undefined) {
  return card?.identityReviewDraft?.importedFrom ?? card?.importedIdentification;
}
export type ImportedResearch = BatchTransferResearch;
export type ImportedListingField = 'title' | 'description' | 'price' | 'features';

/** A research source is a reference, never an automatic lookup or endorsement. */
export function safeResearchSourceUrl(value: string): string | undefined {
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) && !url.username && !url.password
      ? url.href
      : undefined;
  } catch {
    return undefined;
  }
}

/** Seller Hub descriptions can be interpreted as HTML; imported copy is plain text. */
export function escapeImportedDescription(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (character) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]!,
  );
}

/** Imported evidence remains advisory until the reviewer explicitly copies its price. */
export function ImportedPriceSuggestion({
  research,
  onAdopt,
  disabled = false,
  compact = false,
}: {
  research: ImportedResearch;
  onAdopt?: () => void;
  disabled?: boolean;
  compact?: boolean;
}): JSX.Element | null {
  const listing = research.listing;
  if (listing?.suggestedPrice === undefined) return null;
  const sources = (
    <ul>
      {listing.priceSources?.map((source, index) => {
        const href = safeResearchSourceUrl(source.url);
        const label = `${source.kind === 'SOLD' ? 'Reported sold' : source.kind === 'ACTIVE' ? 'Active asking' : 'Other reference'}${source.amountMinor === undefined ? '' : ` · USD ${(source.amountMinor / 100).toFixed(2)}`}`;
        return (
          <li key={index}>
            {href ? (
              <a href={href} target="_blank" rel="noopener noreferrer">
                {label}
              </a>
            ) : (
              <span>{label} · link unavailable</span>
            )}
          </li>
        );
      })}
    </ul>
  );
  return (
    <div className={compact ? 'tcvs-imported-price-suggestion' : undefined}>
      <div className="tcvs-imported-price-heading">
        <strong>
          Suggested price: USD {(listing.suggestedPrice.amountMinor / 100).toFixed(2)}
        </strong>
        {onAdopt ? (
          <button
            type="button"
            className="tcvs-btn-secondary tcvs-btn-compact"
            disabled={disabled}
            onClick={onAdopt}
          >
            Use suggested price
          </button>
        ) : null}
      </div>
      <p className="tcvs-research-text">
        {compact && (listing.priceBasis?.length ?? 0) > 240
          ? `${listing.priceBasis!.slice(0, 240)}…`
          : listing.priceBasis}
      </p>
      <p className="tcvs-muted">
        Research suggestion; active asking prices are not completed sales.
      </p>
      {compact ? (
        (listing.priceBasis?.length ?? 0) > 240 || (listing.priceSources?.length ?? 0) > 0 ? (
          <details>
            <summary>
              Price basis and sources
              {listing.priceSources?.length ? ` (${listing.priceSources.length})` : ''}
            </summary>
            {(listing.priceBasis?.length ?? 0) > 240 ? (
              <p className="tcvs-research-text">{listing.priceBasis}</p>
            ) : null}
            {sources}
          </details>
        ) : null
      ) : (
        sources
      )}
      {compact && (listing.priceBasis?.length ?? 0) > 500 ? (
        <p className="tcvs-muted">
          Using this price copies an abbreviated rationale into pricing notes. The full rationale
          remains here.
        </p>
      ) : null}
    </div>
  );
}

export function ImportedResearchDetails({
  research,
  onAdopt,
  disabledFields = [],
  onReviewCatalogIssue,
  disabled = false,
  catalogWarnings = [],
  showPrice = true,
}: {
  research: ImportedResearch;
  showPrice?: boolean;
  catalogWarnings?: readonly BatchImportCatalogWarning[];
  onAdopt?: (field: ImportedListingField) => void;
  disabledFields?: readonly ImportedListingField[];
  onReviewCatalogIssue?: (type: ChecklistIssueType, note: string) => void;
  disabled?: boolean;
}) {
  const listing = research.listing;
  const adoption = (field: ImportedListingField, label: string) =>
    onAdopt ? (
      <button
        type="button"
        className="tcvs-btn-secondary tcvs-btn-compact"
        disabled={disabled || disabledFields.includes(field)}
        onClick={() => onAdopt(field)}
      >
        Use suggested {label}
      </button>
    ) : null;
  return (
    <div className="tcvs-imported-research-details">
      {catalogWarnings.length ? (
        <section aria-label="Imported checklist differences">
          <strong>Checklist differences retained with manual identification</strong>
          <ul>
            {catalogWarnings.map((warning, index) => (
              <li key={index}>
                {warning.message}
                {warning.field
                  ? ` · ${warning.field}: ${JSON.stringify(warning.current ?? null)} → ${JSON.stringify(warning.proposed ?? null)}`
                  : ''}
                <p>{warning.resolution}</p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {listing ? (
        <section aria-label="Imported listing suggestions">
          <strong>Listing suggestions · unverified</strong>
          {onAdopt ? (
            <p className="tcvs-muted">
              Choose each suggestion to replace that local field, then use Save. Loading or
              importing research never adopts listing fields automatically.
            </p>
          ) : (
            <p className="tcvs-muted">
              Listing suggestions are separate from identity application.
            </p>
          )}
          {listing.title === undefined ? null : (
            <div>
              <strong>Title</strong>
              <p>{listing.title}</p>
              {adoption('title', 'title')}
            </div>
          )}
          {listing.description === undefined ? null : (
            <div>
              <strong>Description (plain text)</strong>
              <p className="tcvs-research-text">{listing.description}</p>
              {adoption('description', 'description')}
            </div>
          )}
          {showPrice ? (
            <ImportedPriceSuggestion
              research={research}
              onAdopt={onAdopt ? () => onAdopt('price') : undefined}
              disabled={disabled || disabledFields.includes('price')}
            />
          ) : null}
          {listing.features === undefined ? null : (
            <div>
              <strong>Features</strong>
              <p>{listing.features.join(', ')}</p>
              {adoption('features', 'features')}
            </div>
          )}
        </section>
      ) : null}
      {research.draftDisagreements?.length ? (
        <section aria-label="Draft disagreements">
          <strong>Differences from operator draft</strong>
          <ul>
            {research.draftDisagreements.map((issue) => (
              <li key={issue.field}>
                <strong>{issue.field}</strong> · {issue.note}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {research.captureIssues?.length ? (
        <section aria-label="Capture warnings">
          <strong>Capture warnings · reviewer action required</strong>
          <ul>
            {research.captureIssues.map((issue, index) => (
              <li key={index}>
                {issue.type.replaceAll('_', ' ')} · {issue.note}
              </li>
            ))}
          </ul>
          <p className="tcvs-muted">These notes do not rotate, swap or approve images.</p>
        </section>
      ) : null}
      {research.imageOrientation ? (
        <section aria-label="Automatic image orientation">
          <strong>Image orientation · applied on Manually confirmed import</strong>
          <ul>
            {(['front', 'back'] as const).map((side) => {
              const fix = research.imageOrientation?.[side];
              if (!fix) return null;
              const parts = [
                fix.quarterTurns ? `turn ${fix.quarterTurns * 90}° clockwise` : '',
                fix.deskewDegrees ? `straighten ${fix.deskewDegrees}°` : '',
              ].filter(Boolean);
              return (
                <li key={side}>
                  {side} · {parts.join(', ')}
                </li>
              );
            })}
          </ul>
          <p className="tcvs-muted">
            TCVS writes a rotated copy of the original photo and keeps the original in the card's
            image history. Draft imports never change images.
          </p>
        </section>
      ) : null}
      {research.catalogIssues?.length ? (
        <section aria-label="Catalog research notes">
          <strong>Checklist research notes</strong>
          <ul>
            {research.catalogIssues.map((issue, index) => (
              <li key={index}>
                <p>
                  {issue.type.replaceAll('_', ' ')} · {issue.note}
                </p>
                {onReviewCatalogIssue ? (
                  <button
                    type="button"
                    className="tcvs-btn-secondary tcvs-btn-compact"
                    disabled={disabled}
                    onClick={() => onReviewCatalogIssue(issue.type, issue.note)}
                  >
                    Review checklist issue
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
          <p className="tcvs-muted">A reviewer must separately submit a checklist issue.</p>
        </section>
      ) : null}
    </div>
  );
}
